import { AVATAR_COLORS } from "./types.js";

/**
 * 이메일 주소를 비교·저장용 표준형으로 바꾼다.
 *
 * 로컬파트 대소문자는 RFC상 의미가 있지만 현실의 메일 서버는 거의 전부
 * 무시하므로, 같은 사람을 두 방으로 쪼개지 않기 위해 소문자로 통일한다.
 *
 * plus 태그(`me+shop@`)는 **여기서는 지우지 않는다.** 이 함수는 상대 주소에도
 * 쓰이는데, 남의 `sender+a@gmail.com`과 `sender+b@gmail.com`을 합쳐버리면
 * 서로 다른 발신자가 한 방에 섞인다.
 *
 * 우리 도메인 주소를 identity로 해석할 때만 태그를 자른다 → toIdentityAddress()
 */
export function normalizeAddress(raw: string): string {
  const trimmed = raw.trim();
  // "이름 <주소>" 형태면 꺾쇠 안쪽만 꺼낸다
  const angled = /<([^>]+)>/.exec(trimmed);
  const bare = (angled?.[1] ?? trimmed).trim();

  const at = bare.lastIndexOf("@");
  if (at <= 0) return bare.toLowerCase();

  const local = bare.slice(0, at);
  const domain = bare.slice(at + 1);
  return `${local.toLowerCase()}@${domain.toLowerCase()}`;
}

/**
 * 우리 도메인으로 온 주소를 identity 키와 plus 태그로 가른다.
 *
 * catch-all이라 어떤 형태로든 들어온다. 태그를 잘라내지 않으면
 * `amazon+shopping@`이 `amazon@`과 다른 identity로 잡혀 정상 메일이 미아가 된다.
 *
 * 태그는 **버리지 않고 돌려준다.** 어느 사이트가 주소를 흘렸는지 추적하는
 * 단서라서, delivery 단위로 남겨둔다. (architecture.md §3.4)
 *
 *   Amazon+shopping@smokat.cc  →  { address: "amazon@smokat.cc", tag: "shopping" }
 *   AMAZON+a+b@smokat.cc       →  { address: "amazon@smokat.cc", tag: "a+b" }
 */
export function toIdentityAddress(raw: string): {
  address: string;
  tag: string | null;
} {
  const normalized = normalizeAddress(raw);
  const at = normalized.lastIndexOf("@");
  if (at <= 0) return { address: normalized, tag: null };

  const local = normalized.slice(0, at);
  const domain = normalized.slice(at + 1);

  const plus = local.indexOf("+");
  if (plus < 0) return { address: normalized, tag: null };

  // 첫 + 이후는 전부 태그다. "a+b"처럼 +가 더 있어도 통째로 둔다.
  const tag = local.slice(plus + 1);
  return {
    address: `${local.slice(0, plus)}@${domain}`,
    // "amazon+@"처럼 태그가 비어 있으면 없는 것으로 본다.
    tag: tag.length > 0 ? tag : null,
  };
}

/** identity 주소에서 로컬파트만. 새 identity를 만들 때 검증에 쓴다. */
export function localPartOf(address: string): string {
  const normalized = normalizeAddress(address);
  const at = normalized.lastIndexOf("@");
  return at <= 0 ? normalized : normalized.slice(0, at);
}

/**
 * identity로 쓸 수 있는 로컬파트인가.
 *
 * RFC가 허용하는 범위보다 좁게 잡는다. 따옴표 붙은 로컬파트나 점 연속처럼
 * 규격상 유효하지만 현실에서 사고만 내는 형태를 미리 막는다.
 */
export function isValidLocalPart(localPart: string): boolean {
  if (localPart.length === 0 || localPart.length > 64) return false;
  // plus는 태그 구분자로 쓰므로 identity 이름에는 넣을 수 없다.
  if (localPart.includes("+")) return false;
  return /^[a-z0-9]([a-z0-9._-]*[a-z0-9])?$/.test(localPart);
}

/** "이름 <주소>" 에서 표시이름만. 없으면 null */
export function displayNameFromHeader(raw: string): string | null {
  const trimmed = raw.trim();
  const angled = trimmed.indexOf("<");
  if (angled <= 0) return null;

  const name = trimmed.slice(0, angled).trim().replace(/^"|"$/g, "").trim();
  return name.length > 0 ? name : null;
}

/** 답장을 받을 수 없는 발신전용 주소인가 */
export function isNoReplyAddress(address: string): boolean {
  const local = normalizeAddress(address).split("@")[0] ?? "";
  return /^(no-?reply|do-?not-?reply|noreply|donotreply|notification|notifications|mailer-daemon|postmaster|bounce)/.test(
    local,
  );
}

const HANGUL = /[가-힣]/;

/**
 * 아바타에 넣을 이니셜.
 *
 * 시안의 실제 값들을 보면 규칙이 하나로 떨어지지 않아(최은비→"최", 쿠팡→"쿠팡",
 * EBS 교육→"EBS") 아래 휴리스틱으로 근사한다. 사용자가 이름을 직접 바꾸면
 * 그 값으로 다시 계산된다.
 *
 * - 한글 2~3자 + 흔한 성씨로 시작 → 사람 이름으로 보고 성 1자
 * - 그 외 한글 → 앞 2자
 * - 전체 대문자 영문 약어 → 앞 3자까지
 * - 그 외 영문 → 첫 글자 1자
 */
export function initialsFor(displayName: string): string {
  const name = displayName.trim();
  if (name.length === 0) return "?";

  const first = name[0] ?? "";

  if (HANGUL.test(first)) {
    const looksLikePersonName = name.length <= 3 && KOREAN_SURNAMES.has(first);
    return looksLikePersonName ? first : name.slice(0, 2);
  }

  const firstWord = name.split(/\s+/)[0] ?? name;
  // 짧은 전체 대문자만 약어로 본다(EBS, KB). "SEONG CHAN SEOL"처럼 이름을
  // 대문자로 쓴 경우까지 잡으면 "SEO" 같은 이상한 이니셜이 나온다.
  if (/^[A-Z]{2,4}$/.test(firstWord)) return firstWord;

  return first.toUpperCase();
}

/**
 * 주소별로 아바타 색을 고정 배정한다.
 * 같은 사람은 언제 어느 기기에서 봐도 같은 색이어야 하므로 난수를 쓰지 않는다.
 */
export function colorIndexFor(address: string): number {
  const key = normalizeAddress(address);
  // FNV-1a 32비트. 짧고 분포가 고르다.
  let hash = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash % AVATAR_COLORS.length;
}

/** 자주 쓰이는 한국 성씨. 이니셜 휴리스틱에만 쓰이므로 완전할 필요는 없다. */
const KOREAN_SURNAMES = new Set([
  "김", "이", "박", "최", "정", "강", "조", "윤", "장", "임",
  "한", "오", "서", "신", "권", "황", "안", "송", "류", "전",
  "홍", "고", "문", "양", "손", "배", "백", "허", "유", "남",
  "심", "노", "하", "곽", "성", "차", "주", "우", "구", "민",
  "지", "엄", "채", "원", "천", "방", "공", "현", "함", "변",
  "염", "여", "추", "도", "소", "석", "선", "설", "마", "길",
]);
